import React, { useState, useEffect } from 'react';
import {
    LineChart, Line, BarChart, Bar, PieChart, Pie, AreaChart, Area,
    XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import './analytics.css';

const Analytics = () => {
    const [timeFrame, setTimeFrame] = useState('year');
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState({
        users: [],
        orders: [],
        warranty: [],
        revenue: [],
        abandonedCart: []
    });

    const getCurrentDateFilters = () => {
        const now = new Date();
        const year = now.getFullYear();
        const month = now.getMonth() + 1;
        return { year, month };
    };

    const fetchData = async () => {
        try {
            setLoading(true);

            const { year, month } = getCurrentDateFilters();
            let url = `${process.env.REACT_APP_PROD_URL}/ColorBotAdminGetAnalyticsDashboard?year=${year}`;
            if (timeFrame === 'month') {
                url += `&month=${month}`;
            }

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${window.localStorage.getItem('token')}`
                }
            });

            const result = await response.json();

            if (result.status === 200) {
                const charts = result.data?.charts || {};

                setData({
                    users: charts.monthlyUserData || [],
                    orders: charts.monthlyOrderStatus || [],
                    warranty: charts.monthlyWarranty || [],
                    revenue: charts.monthlyRevenue || [],
                    abandonedCart: charts.monthlyAbandonedCarts || [],
                });
            } else {
                console.error("Failed to load analytics data");
            }
        } catch (error) {
            console.error("Analytics fetch error:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [timeFrame]);

    return (
        <section className="dashbg-color">
            <div className="row">
                <div className="col-lg-24">
                    <br />
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <h3 className="fw-bold">Analytics Dashboard</h3>
                        <select
                            className="form-select w-auto"
                            value={timeFrame}
                            onChange={(e) => setTimeFrame(e.target.value)}
                        >
                            <option value="day" disabled>Daily (not implemented)</option>
                            <option value="week" disabled>Weekly (not implemented)</option>
                            <option value="month">Monthly</option>
                            <option value="year">Yearly</option>
                        </select>
                    </div>

                    {loading ? (
                        <div className="text-center my-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    ) : (
                        <div className="row g-4">
                            <div className="col-md-6">
                                <div className="analytics-card">
                                    <h5 className="card-title">User Growth</h5>
                                    <ResponsiveContainer width="100%" height={300}>
                                        <LineChart data={data.users}>
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis dataKey="name" />
                                            <YAxis />
                                            <Tooltip />
                                            <Legend />
                                            <Line type="monotone" dataKey="value" stroke="#8884d8" />
                                        </LineChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>

                            <div className="col-md-6">
                                <div className="analytics-card">
                                    <h5 className="card-title">Orders Status</h5>
                                    <ResponsiveContainer width="100%" height={300}>
                                        <BarChart data={data.orders}>
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis dataKey="name" />
                                            <YAxis />
                                            <Tooltip />
                                            <Legend />
                                            <Bar dataKey="delivered" fill="#82ca9d" />
                                            <Bar dataKey="rto" fill="#8884d8" />
                                            <Bar dataKey="cancelled" fill="#ffc658" />
                                            <Bar dataKey="pending" fill="#ff8042" />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>

                            <div className="col-md-6">
                                <div className="analytics-card">
                                    <h5 className="card-title">Warranty Status</h5>
                                    <ResponsiveContainer width="100%" height={300}>
                                        <AreaChart data={data.warranty}>
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis dataKey="name" />
                                            <YAxis />
                                            <Tooltip />
                                            <Legend />
                                            <Area type="monotone" dataKey="claims" fill="#8884d8" />
                                            <Area type="monotone" dataKey="registered" fill="#82ca9d" />
                                        </AreaChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>

                            <div className="col-md-6">
                                <div className="analytics-card">
                                    <h5 className="card-title">Revenue</h5>
                                    <ResponsiveContainer width="100%" height={300}>
                                        <AreaChart data={data.revenue}>
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis dataKey="name" />
                                            <YAxis />
                                            <Tooltip />
                                            <Legend />
                                            <Area type="monotone" dataKey="value" fill="#82ca9d" />
                                        </AreaChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>

                            <div className="col-12">
                                <div className="analytics-card">
                                    <h5 className="card-title">Abandoned Cart Analysis</h5>
                                    <ResponsiveContainer width="100%" height={300}>
                                        <PieChart>
                                            <Pie
                                                data={data.abandonedCart}
                                                dataKey="value"
                                                nameKey="name"
                                                cx="50%"
                                                cy="50%"
                                                outerRadius={100}
                                                fill="#8884d8"
                                                label
                                            />
                                            <Tooltip />
                                            <Legend />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>
                        </div>
                    )}

                    <br />
                    <br />
                </div>
            </div>
        </section>
    );
};

export default Analytics;
